import { useCallback, useEffect, useState } from 'react';
import { Box, IconButton } from '@material-ui/core';
import { Icon } from '@iconify/react';
import campaign from '@iconify/icons-ic/baseline-campaign';
import closeFill from '@iconify/icons-eva/close-fill';
import axios from 'axios';
import { REST_API } from 'src/constants/DefaultValues';

export const BANNER_SETTINGS_UPDATED_EVENT = 'mayura-banner-settings-updated';

function AnimatedAnnouncementIcon() {
  return (
    <Box
      component="span"
      sx={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 22,
        height: 22,
        flexShrink: 0,
      }}
    >
      <Box
        component={Icon}
        icon={campaign}
        width={18}
        height={18}
        sx={{
          color: '#fff',
          transformOrigin: 'center center',
          animation: 'announceShake 1.4s ease-in-out infinite',
          '@keyframes announceShake': {
            '0%, 100%': { transform: 'rotate(0deg)' },
            '10%': { transform: 'rotate(-14deg)' },
            '20%': { transform: 'rotate(12deg)' },
            '30%': { transform: 'rotate(-10deg)' },
            '40%': { transform: 'rotate(8deg)' },
            '50%': { transform: 'rotate(-4deg)' },
            '60%': { transform: 'rotate(0deg)' },
          },
        }}
      />
      <Box
        component="span"
        sx={{
          position: 'absolute',
          top: -1,
          right: -2,
          width: 7,
          height: 7,
          borderRadius: '50%',
          bgcolor: '#ff1744',
          border: '1px solid #fff',
          animation: 'announcePulse 1.2s ease-in-out infinite',
          '@keyframes announcePulse': {
            '0%, 100%': { transform: 'scale(1)', opacity: 1 },
            '50%': { transform: 'scale(1.35)', opacity: 0.75 },
          },
        }}
      />
    </Box>
  );
}

function BannerItem({ text }) {
  return (
    <Box
      component="span"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        whiteSpace: 'nowrap',
        px: 1.5,
        color: '#fff',
        fontWeight: 700,
        fontSize: { xs: '0.85rem', sm: '0.95rem' },
      }}
    >
      <AnimatedAnnouncementIcon />
      <Box component="span">{text}</Box>
      <Box component="span" sx={{ opacity: 0.85, fontSize: '1.1em', lineHeight: 1 }}>
        •
      </Box>
    </Box>
  );
}

export default function AnnouncementBanner({ variant = 'dashboard', width = '100%' }) {
  const [bannerText, setBannerText] = useState('');
  const [visible, setVisible] = useState(false);

  const fetchBanner = useCallback(async () => {
    try {
      const response = await axios.get(`${REST_API}settings/get-settings`);
      if (response.data.status !== 1) return;

      const data = response.data.data || {};
      const enabled = Number(data.show_banner) === 1;
      const text = (data.banner_text || '').trim();

      if (!enabled || !text) {
        setBannerText('');
        setVisible(false);
        return;
      }

      setBannerText(text);
      setVisible(true);
    } catch (error) {
      console.error('Error fetching banner settings:', error);
    }
  }, []);

  useEffect(() => {
    fetchBanner();

    const handleSettingsUpdated = () => {
      fetchBanner();
    };

    const handlePageShow = () => {
      // Re-show after browser refresh / back-forward cache restore
      fetchBanner();
    };

    window.addEventListener(BANNER_SETTINGS_UPDATED_EVENT, handleSettingsUpdated);
    window.addEventListener('pageshow', handlePageShow);

    return () => {
      window.removeEventListener(BANNER_SETTINGS_UPDATED_EVENT, handleSettingsUpdated);
      window.removeEventListener('pageshow', handlePageShow);
    };
  }, [fetchBanner, variant]);

  const handleDismiss = () => {
    setVisible(false);
  };

  if (!visible || !bannerText) return null;

  const items = Array.from({ length: 8 }, (_, i) => (
    <BannerItem key={`${bannerText}-${i}`} text={bannerText} />
  ));

  const isLogin = variant === 'login';

  return (
    <Box
      sx={{
        display: { xs: 'none', sm: 'block' },
        position: 'relative',
        width: width || '100%',
        mb: 2,
        borderRadius: isLogin ? 10 : 1,
        overflow: 'hidden',
        background: 'linear-gradient(90deg, #c2410c 0%, #ea580c 35%, #f97316 50%, #ea580c 65%, #c2410c 100%)',
        boxShadow: isLogin ? '0 4px 14px rgba(194, 65, 12, 0.35)' : 'none',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          height: isLogin ? 42 : 44,
          overflow: 'hidden',
          pr: variant === 'dashboard' ? 5 : 0,
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: 'max-content',
            animation: 'bannerMarquee 90s linear infinite',
            '@keyframes bannerMarquee': {
              '0%': { transform: 'translateX(0)' },
              '100%': { transform: 'translateX(-50%)' },
            },
          }}
        >
          {items}
          {items}
        </Box>
      </Box>

      {variant === 'dashboard' && (
        <IconButton
          size="small"
          onClick={handleDismiss}
          aria-label="Dismiss banner"
          sx={{
            position: 'absolute',
            right: 4,
            top: '50%',
            transform: 'translateY(-50%)',
            color: '#fff',
            p: 0.5,
            '&:hover': { bgcolor: 'rgba(255,255,255,0.15)' },
          }}
        >
          <Icon icon={closeFill} width={18} height={18} />
        </IconButton>
      )}
    </Box>
  );
}
